import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../config/apiBase";

const PROFILE_UPDATED_EVENT = "mooz:profileUpdated";
const PROFILE_CACHE_KEY = "mooz:apiProfile";

function readCachedProfile() {
    try {
        const raw = localStorage.getItem(PROFILE_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeCachedProfile(profile) {
    try {
        if (!profile) localStorage.removeItem(PROFILE_CACHE_KEY);
        else localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    } catch {
        /* noop */
    }
}

export default function useApiProfile({ enabled = true, syncOn404 = true } = {}) {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const [profile, setProfile] = useState(() =>
        typeof window === "undefined" ? null : readCachedProfile()
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const onUpdated = (e) => {
            const next = e?.detail;
            if (next && typeof next === "object") {
                setProfile((prev) => {
                    const merged = { ...(prev || {}), ...next };
                    writeCachedProfile(merged);
                    return merged;
                });
            }
        };
        window.addEventListener(PROFILE_UPDATED_EVENT, onUpdated);
        return () =>
            window.removeEventListener(PROFILE_UPDATED_EVENT, onUpdated);
    }, []);

    const refresh = useCallback(async (allowSync = syncOn404) => {
        if (!enabled) return null;
        if (isLoading) return null;
        if (!isAuthenticated) {
            setProfile(null);
            writeCachedProfile(null);
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            const res = await fetch(`${API_BASE}/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                if (res.status === 404 && allowSync) {
                    const syncRes = await fetch(
                        `${API_BASE}/auth/sync`,
                        {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    if (syncRes.ok) return await refresh(false);
                }
                const text = await res.text().catch(() => "");
                const err = new Error(
                    text || `Failed to fetch profile (${res.status})`
                );
                err.status = res.status;
                throw err;
            }
            const data = await res.json();
            setProfile(data);
            writeCachedProfile(data);
            return data;
        } catch (e) {
            setError(e);
            return null;
        } finally {
            setLoading(false);
        }
    }, [enabled, getAccessTokenSilently, isAuthenticated, isLoading, syncOn404]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!enabled || isLoading) return;
            if (!isAuthenticated) {
                setProfile(null);
                setError(null);
                setLoading(false);
                return;
            }
            const data = await refresh(syncOn404);
            if (cancelled) return;
            return data;
        })();
        return () => {
            cancelled = true;
        };
    }, [enabled, isAuthenticated, isLoading, refresh, syncOn404]);

    return { profile, loading, error, refresh };
}
